import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { GQL } from '@aline/common';
import { link, params } from '@aline/routes';
import { DndContext, MouseSensor, useSensor, useSensors } from '@dnd-kit/core';
import { useNavigate } from 'react-router-dom';
import { Point } from 'slate';
import { anchorPaddingPx } from 'src/components/drag-and-drop/modifiers';
import {
  defaultPDFFieldAttributes,
  getPDFPageCoords,
  getPdfStampDefaults,
  pdfCheckboxRowTopOffsetPx,
  ResolveToPageLocation,
} from 'src/components/viewer';
import { getIsSignerFieldRecipient } from 'src/components/workflow';
import { useMaybeEditorRuntimeForTabId } from 'src/editor/editor-runtime-registry';
import {
  getEditorTabStateActionsForTabId,
  useEditorTabStateSelectorForTabId,
} from 'src/editor/editor-tab-state-registry';
import type { EditorMousePageCoords } from 'src/editor/editor-tab-state-registry';
import { Queries } from 'src/editor/queries';
import { getPathWithDrawerSearchParam } from 'src/editor/toolbars/right-panel/drawer-search-params';
import { Apollo } from 'src/gql/generated';
import { domElIdCreators } from 'src/helpers';
import type { FieldLocation } from 'src/hooks';
import {
  getFieldLabelFromVariant,
  useCreateField,
  useGetCurrentDoc,
  useInsertCannedField,
  useInsertFieldIntoDocument,
  useSetFieldValue,
} from 'src/hooks';
import { useGlobalDrawer } from 'src/layouts/GlobalRightDrawer';

import { AlineDragOverlay } from './AlineDragOverlay';
import type { DragField } from './helpers';
import { createDetectDragPreviewCollisions, DragFieldSchema, DropFieldSchema } from './helpers';

interface AlineDNDEditorContextProps {
  activeTabId: UUID | null;
  children: React.ReactNode;
}

export function AlineDNDEditorContext({
  activeTabId,
  children,
}: AlineDNDEditorContextProps): JSX.Element {
  const runtime = useMaybeEditorRuntimeForTabId(activeTabId);
  const navigate = useNavigate();
  const { openDrawer } = useGlobalDrawer();
  const editor = runtime?.editor ?? null;
  const isHeaderOpen = useEditorTabStateSelectorForTabId(
    activeTabId,
    state => state.documentUi.headerOpen
  );
  const isFooterOpen = useEditorTabStateSelectorForTabId(
    activeTabId,
    state => state.documentUi.footerOpen
  );
  const editingMode = useEditorTabStateSelectorForTabId(
    activeTabId,
    state => state.documentUi.editingMode
  );
  const documentViewer = runtime?.documentViewer ?? null;
  const documentId = runtime?.documentId ?? null;
  const { setFieldValue } = useSetFieldValue(editor, documentViewer, activeTabId);
  const { createField } = useCreateField({
    documentId,
    documentViewer,
    editingMode,
    editor,
    tabId: activeTabId,
  });
  const { insertCannedField } = useInsertCannedField(documentViewer, activeTabId);
  const sensors = useSensors(
    useSensor(MouseSensor, {
      // Require the mouse to move by 10 pixels before activating so that onClick handlers aren't swallowed on the draggable element (e.g., InputFieldButton).
      activationConstraint: {
        distance: 10,
      },
    })
  );
  const fieldRecipient = useEditorTabStateSelectorForTabId(
    activeTabId,
    state => state.documentUi.fieldRecipient
  );
  const mousePageCoords = useEditorTabStateSelectorForTabId(
    activeTabId,
    state => state.dragDrop.mousePageCoords
  );
  const { document: alineDocument } = useGetCurrentDoc(documentId);
  const [dragField, setDragField] = useState<DragField | null>(null);
  const { insertFieldIntoDocument } = useInsertFieldIntoDocument({
    documentViewer,
    editingMode,
    editor,
    tabId: activeTabId,
  });
  const [dropAnimation, setDropAnimation] = useState<null | undefined>(undefined);
  const collisionDetection = useMemo(
    () => createDetectDragPreviewCollisions(activeTabId),
    [activeTabId]
  );
  const setMousePageCoords = useCallback(
    (mousePageCoords: EditorMousePageCoords | null) => {
      if (!activeTabId) return;
      getEditorTabStateActionsForTabId(activeTabId).dragDrop.setMousePageCoords(mousePageCoords);
    },
    [activeTabId]
  );

  useEffect(() => {
    /**
     * Tabs stay mounted/frozen, so dnd-kit can keep stale drag state or droppable
     * measurements from the previous tab. Clear the active drag whenever the
     * tab-scoped runtime target changes.
     */
    setDragField(null);
    setMousePageCoords(null);
    setDropAnimation(undefined);
    return () => setMousePageCoords(null);
  }, [activeTabId, documentId, documentViewer, editor, setMousePageCoords]);

  function openNewFieldDetail(fieldId: UUID): void {
    if (!documentId) return;

    navigate(
      getPathWithDrawerSearchParam(
        link.DocumentDetailAddInput({ documentId }),
        window.location.search,
        params.rightPanel.newFieldId,
        fieldId
      ),
      { replace: true }
    );
    openDrawer();
  }

  return (
    <DndContext
      // autoScroll makes right panel scroll weirdly...
      autoScroll={false}
      collisionDetection={collisionDetection}
      sensors={sensors}
      onDragEnd={async () => {
        setDragField(null);
        setMousePageCoords(null);
        if (!dragField || !alineDocument || !mousePageCoords) return;

        // Temp turn return animation off b/c we are handling the drop.
        setDropAnimation(null);

        const isPoint = Point.isPoint(mousePageCoords);
        const isSignature = dragField.__typename === 'SignatureField';
        const isCanned = dragField.__typename === 'CannedField';
        const isCheckboxPdfDrop =
          !isPoint &&
          dragField.__typename === 'DocumentVariable' &&
          dragField.variant === Apollo.MetadataVariant.Checkbox;
        const locationInfo: FieldLocation = !isPoint
          ? {
              __typename: 'StaticFileVersion',
              at: {
                pageNum: mousePageCoords.eventPageNum,
                // For PDF drops, we store `mousePageCoords.pageCoords` as the intended field top-left in page coords.
                pageX: mousePageCoords.pageCoords.x,
                pageY:
                  mousePageCoords.pageCoords.y -
                  (isCheckboxPdfDrop ? pdfCheckboxRowTopOffsetPx : 0),
              },
            }
          : {
              __typename: 'DocumentVersion',
              at: { anchor: mousePageCoords, focus: mousePageCoords },
            };

        if (isCanned) {
          const workflowId = alineDocument?.workflow?.id ?? null;
          if (!workflowId || !documentId) return;
          if (isPoint) {
            if (editor && editingMode)
              await insertCannedField({
                assigneeId: dragField.assigneeId,
                documentId,
                nodeId: dragField.nodeId,
                target: {
                  __typename: 'Slate',
                  at: { anchor: mousePageCoords, focus: mousePageCoords },
                  editingMode,
                  editor,
                },
                variant: dragField.variant,
                workflowId,
              });
          } else {
            await insertCannedField({
              assigneeId: dragField.assigneeId,
              documentId,
              nodeId: dragField.nodeId,
              target: {
                __typename: 'Pdf',
                location: {
                  pageNum: mousePageCoords.eventPageNum,
                  pageX: mousePageCoords.pageCoords.x,
                  pageY: mousePageCoords.pageCoords.y,
                },
              },
              variant: dragField.variant,
              workflowId,
            });
          }
        } else if (isSignature) {
          await createField({
            __typename: 'SignatureFieldForm',
            action:
              fieldRecipient && getIsSignerFieldRecipient(fieldRecipient) ? fieldRecipient : null,
            locationInfo,
            nodeId: dragField.nodeId,
            signatureVariant: dragField.signatureVariant,
          });
        } else {
          switch (dragField.actionType) {
            case 'create': {
              const isCheckbox = dragField.variant === Apollo.MetadataVariant.Checkbox;
              const { field } = await createField({
                __typename: 'VariableForm',
                action: fieldRecipient,
                integrationLink: dragField.integrationLink,
                isDynamic: dragField.isDynamic,
                label: dragField.label,
                locationInfo,
                nodeId: dragField.nodeId,
                ...(dragField.options ? { options: dragField.options } : {}),
                ...(isCheckbox
                  ? { selectedAnswerIds: dragField.selectedAnswerIds ?? [], value: null }
                  : { value: dragField.value }),
                variant: dragField.variant,
              });
              if (field) openNewFieldDetail(field.id);

              break;
            }
            case 'insert': {
              const isCheckbox = dragField.variant === Apollo.MetadataVariant.Checkbox;
              await insertFieldIntoDocument({
                isDynamic: dragField.isDynamic,
                locationInfo,
                ...(isCheckbox
                  ? {
                      checkboxOptions: dragField.checkboxOptions ?? [],
                      selectedAnswerIds: dragField.selectedAnswerIds ?? [],
                    }
                  : {}),
                newField: {
                  __typename: 'DocumentVariable',
                  label: dragField.label || getFieldLabelFromVariant(dragField.variant),
                  multiLine: false,
                  nodeId: dragField.nodeId,
                  value: isCheckbox ? null : dragField.value,
                  variant: dragField.variant,
                },
              });
              /**
               * Set field value b/c there may be other fields in the doc and the values might not be synced.
               * This ensures all field values are synced/the same in the entire doc.
               */
              if (!isCheckbox && dragField.value)
                setFieldValue({
                  value: dragField.value,
                  variable: {
                    __typename: 'DocumentVariable',
                    action: null,
                    nodeId: dragField.nodeId,
                  },
                  versionTypename: isPoint ? 'DocumentVersion' : 'StaticFileVersion',
                });

              break;
            }
          }
        }

        // Reset drop animation back to default.
        setTimeout(() => setDropAnimation(undefined));
      }}
      onDragMove={event => {
        const { over } = event;
        const dragEl = document.getElementById(domElIdCreators.fieldOverlayEl());
        if (!dragField || !dragEl || !over) return setMousePageCoords(null);

        const dragElRect = dragEl.getBoundingClientRect();
        const { width, x, y } = dragElRect;
        const parsedDropField = DropFieldSchema.safeParse(over.data.current);
        // Dragging into an Aline page...
        if (parsedDropField.success) {
          if (!editor?.renderModel) return setMousePageCoords(null);

          const { pageNumber, rMPageEntry } = parsedDropField.data;
          const containerEl = document.getElementById(
            domElIdCreators.pageEl(parsedDropField.data.tabId, pageNumber)
          );
          if (!containerEl) return setMousePageCoords(null);

          const { x: containerLeftPx, y: containerTopPx } = containerEl.getBoundingClientRect();
          const mouseX = x + width - anchorPaddingPx;
          const mouseY = y + anchorPaddingPx;
          const pointDetails = Queries.resolvePointerToPoint(editor, {
            isFooterOpen,
            isHeaderOpen,
            pointerX: mouseX - containerLeftPx,
            pointerY: mouseY - containerTopPx,
            rMElementEntry: rMPageEntry,
          });
          if (!pointDetails) return setMousePageCoords(null);

          const { affinity, cMPoint: point } = pointDetails;
          return setMousePageCoords({ ...point, affinity });
        }

        if (!documentViewer) return setMousePageCoords(null);

        const previewRect = getPDFDragPreviewRect({
          dragElRect,
          dragField,
          zoom: documentViewer.getZoomLevel(),
        });
        const topLeftCoords = getPDFPageCoords(documentViewer, {
          event: previewRect.topLeft,
          resolveTo: ResolveToPageLocation.LocationInPage,
        });
        const bottomRightCoords = getPDFPageCoords(documentViewer, {
          event: previewRect.bottomRight,
          resolveTo: ResolveToPageLocation.LocationInPage,
        });

        if (!topLeftCoords || !bottomRightCoords) return setMousePageCoords(null);
        if (topLeftCoords.eventPageNum !== bottomRightCoords.eventPageNum)
          return setMousePageCoords(null);

        const pageNum = topLeftCoords.eventPageNum;
        const pageWidth = documentViewer.getPageWidth(pageNum);
        const pageHeight = documentViewer.getPageHeight(pageNum);
        const pageX = Math.min(topLeftCoords.pageCoords.x, bottomRightCoords.pageCoords.x);
        const pageY = Math.min(topLeftCoords.pageCoords.y, bottomRightCoords.pageCoords.y);
        /**
         * Use the max of each axis from both corners. Rotation swaps which axis carries the field's
         * width vs. height, so adding screenPx to pageCoords would give wrong extents at 90deg/270deg.
         */
        const isOutOfBounds =
          pageX < 0 ||
          pageY < 0 ||
          Math.max(topLeftCoords.pageCoords.x, bottomRightCoords.pageCoords.x) > pageWidth ||
          Math.max(topLeftCoords.pageCoords.y, bottomRightCoords.pageCoords.y) > pageHeight;

        return setMousePageCoords(
          !isOutOfBounds
            ? {
                eventPageNum: pageNum,
                pageCoords: { x: pageX, y: pageY },
                windowCoords: topLeftCoords.windowCoords,
              }
            : null
        );
      }}
      onDragStart={event => {
        const dragField = DragFieldSchema.parse(event.active.data.current);
        setDragField(dragField);
      }}
    >
      {children}
      <AlineDragOverlay
        documentViewer={documentViewer}
        dragFieldTuple={[dragField, setDragField]}
        dropAnimation={dropAnimation}
        mousePageCoords={mousePageCoords}
      />
    </DndContext>
  );
}

function getPDFDragPreviewRect(args: { dragElRect: DOMRect; dragField: DragField; zoom: number }) {
  const { dragElRect, dragField, zoom } = args;
  const cursorX = dragElRect.x + dragElRect.width - anchorPaddingPx;
  const cursorY = dragElRect.y + anchorPaddingPx;
  const dimensions = getPDFFieldPreviewDimensions(dragField, zoom);
  const left = cursorX - dimensions.widthPx + anchorPaddingPx;
  const top = cursorY - anchorPaddingPx;

  return {
    bottomRight: {
      clientX: left + dimensions.widthPx,
      clientY: top + dimensions.heightPx,
    },
    topLeft: {
      clientX: left,
      clientY: top,
    },
  };
}

function getPDFFieldPreviewDimensions(dragField: DragField, zoom: number) {
  if (dragField.__typename === 'SignatureField') {
    const stampDefaults = getPdfStampDefaults(
      dragField.signatureVariant ?? GQL.SignatureFieldVariant.Signature
    );
    return {
      heightPx: stampDefaults.heightPx * zoom,
      widthPx: stampDefaults.widthPx * zoom,
    };
  }

  if (
    dragField.__typename === 'DocumentVariable' &&
    dragField.variant === Apollo.MetadataVariant.Checkbox
  ) {
    const widgetSidePx = defaultPDFFieldAttributes.fontSizePx;

    return {
      heightPx: widgetSidePx * zoom,
      widthPx: widgetSidePx * zoom,
    };
  }

  return {
    heightPx: defaultPDFFieldAttributes.nonSigFieldHeightPx * zoom,
    widthPx: defaultPDFFieldAttributes.nonSigFieldWidthPx * zoom,
  };
}
